import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBackOutlined as BackIcon, SaveOutlined as SaveIcon, LabelOutlined as TagIcon } from '@mui/icons-material';
import {
  fetchTemplate, fetchPlaceholders, fetchSchemaFields,
  fetchSpecialFields, updateTemplateMapping
} from './api';
import { DocumentTemplateRead, FieldPath, PlaceholdersResponse, MappingEntrySchema, ManualFieldSchema } from './types';

type TagConfig =
  | { mode: 'unmapped' }
  | { mode: 'db_field', path: string, is_special: boolean, cardinality: 'scalar' | 'collection' }
  | { mode: 'manual', label: string, type: string, required: boolean };

const TemplateMapping: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<DocumentTemplateRead | null>(null);
  const [placeholdersData, setPlaceholdersData] = useState<PlaceholdersResponse | null>(null);
  const [schemaFields, setSchemaFields] = useState<FieldPath[]>([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [config, setConfig] = useState<Record<string, TagConfig>>({});
  const [activeTag, setActiveTag] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [tpl, phData, normalFields, specialFields] = await Promise.all([
          fetchTemplate(Number(id)),
          fetchPlaceholders(Number(id)),
          fetchSchemaFields(),
          fetchSpecialFields(),
        ]);

        setTemplate(tpl);
        setPlaceholdersData(phData);
        setSchemaFields([...specialFields, ...normalFields]);

        const initialConfig: Record<string, TagConfig> = {};
        const allTags = Object.keys(phData.placeholders);

        allTags.forEach(tag => {
          if (tpl.data_mapping && tpl.data_mapping[tag]) {
            const mapData = tpl.data_mapping[tag];
            initialConfig[tag] = {
              mode: 'db_field',
              path: mapData.field_path,
              is_special: mapData.type === 'special',
              cardinality: mapData.cardinality || 'scalar'
            };
          } else if (tpl.manual_fields && tpl.manual_fields[tag]) {
            const manualData = tpl.manual_fields[tag];
            initialConfig[tag] = {
              mode: 'manual',
              label: manualData.label || tag,
              type: manualData.type || 'string',
              required: manualData.required || false
            };
          } else {
            initialConfig[tag] = { mode: 'unmapped' };
          }
        });

        setConfig(initialConfig);
        if (allTags.length > 0) setActiveTag(allTags[0]);

      } catch (error) {
        console.error(error);
        alert('Ошибка загрузки данных для маппинга');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id]);

  const handleSave = async () => {
    if (!template) return;
    setSaving(true);
    try {
      const mappings: Record<string, MappingEntrySchema> = {};
      const manual_fields: Record<string, ManualFieldSchema> = {};

      Object.entries(config).forEach(([tag, tagConf]) => {
        if (tagConf.mode === 'db_field') {
          mappings[tag] = {
            type: tagConf.is_special ? 'special' : 'field',
            field_path: tagConf.path,
            cardinality: tagConf.cardinality,
          };
        } else if (tagConf.mode === 'manual') {
          manual_fields[tag] = {
            label: tagConf.label,
            type: tagConf.type,
            required: tagConf.required,
          };
        }
      });

      await updateTemplateMapping(template.id, { mappings, manual_fields });
      alert('Настройки успешно сохранены!');
      navigate('/documents/templates');
    } catch (error: any) {
      alert(`Ошибка сохранения: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const updateActiveConfig = (newConf: Partial<TagConfig>) => {
    if (!activeTag) return;
    setConfig(prev => {
      const current = prev[activeTag];
      if (newConf.mode === 'db_field') {
        return { ...prev, [activeTag]: { ...current, ...newConf } as TagConfig };
      } else if (newConf.mode === 'manual') {
        return { ...prev, [activeTag]: { ...current, ...newConf } as TagConfig };
      } else {
        return { ...prev, [activeTag]: { mode: 'unmapped' } };
      }
    });
  };

  if (loading) return <div className="p-8">Загрузка маппера...</div>;
  if (!template || !placeholdersData) return <div className="p-8 text-red-500">Ошибка загрузки</div>;

  const tags = Object.keys(placeholdersData.placeholders);
  const activeConfig = activeTag ? config[activeTag] : null;

  return (
    <div className="w-full bg-white pb-10">
      {/* h-[calc(100vh-6rem)] внутри обертки обеспечивает высоту сплит-экрана */}
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8 flex flex-col h-[calc(100vh-6rem)]">

        {/* Header */}
        <div className="flex justify-between items-center mb-6 shrink-0">
          <div>
            <button
              onClick={() => navigate('/documents/templates')}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 mb-2 transition-colors"
            >
              <BackIcon fontSize="small" /> Назад к списку
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              Настройка связей: <span className="text-[#1E61AF]">{template.name}</span>
            </h1>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-[#1E61AF] text-white px-5 py-2.5 rounded-lg flex items-center gap-2 hover:bg-blue-800 transition-colors disabled:opacity-50 font-medium"
          >
            <SaveIcon fontSize="small" />
            {saving ? 'Сохранение...' : 'Сохранить настройки'}
          </button>
        </div>

        {/* Split View (на всю ширину) */}
        <div className="flex flex-1 gap-6 overflow-hidden">
          {/* Left: Tags List */}
          <div className="w-1/3 bg-white border border-gray-200 rounded-xl flex flex-col overflow-hidden shadow-sm">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 shrink-0">
              <h3 className="font-semibold text-gray-700">Переменные в документе ({tags.length})</h3>
            </div>
            <div className="overflow-y-auto flex-1 p-2 space-y-1">
              {tags.length === 0 && <p className="text-gray-500 p-4 text-sm">Тегов не найдено</p>}
              {tags.map(tag => {
                const conf = config[tag];
                const isMapped = conf.mode !== 'unmapped';
                return (
                  <div
                    key={tag}
                    onClick={() => setActiveTag(tag)}
                    className={`px-3 py-3 rounded-lg cursor-pointer border flex justify-between items-center transition-all ${
                      activeTag === tag
                        ? 'bg-blue-50 border-blue-200 shadow-sm'
                        : 'border-transparent hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <TagIcon fontSize="small" className={isMapped ? 'text-blue-500' : 'text-gray-400'} />
                      <span className={`font-mono text-sm truncate ${activeTag === tag ? 'text-blue-900 font-semibold' : 'text-gray-700'}`}>
                        {tag}
                      </span>
                    </div>
                    <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                      conf.mode === 'db_field' ? 'bg-green-500' :
                      conf.mode === 'manual' ? 'bg-purple-500' : 'bg-gray-300'
                    }`} title={conf.mode} />
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Editor */}
          <div className="flex-1 bg-white border border-gray-200 rounded-xl flex flex-col shadow-sm">
            {activeTag && activeConfig ? (
              <div className="p-6 flex flex-col h-full">
                <div className="mb-6">
                  <h2 className="text-lg font-bold text-gray-900 font-mono bg-gray-100 inline-block px-3 py-1 rounded">
                    {`{{ ${activeTag} }}`}
                  </h2>
                  <p className="text-sm text-gray-500 mt-2">
                    Откуда система должна брать значение для этого тега?
                  </p>
                </div>

                <div className="flex gap-4 mb-8 border-b border-gray-200 pb-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mode"
                      checked={activeConfig.mode === 'db_field'}
                      onChange={() => updateActiveConfig({ mode: 'db_field', path: '', is_special: false, cardinality: 'scalar' })}
                      className="text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                    <span className="text-sm font-medium">Взять из Базы Данных</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mode"
                      checked={activeConfig.mode === 'manual'}
                      onChange={() => updateActiveConfig({ mode: 'manual', label: activeTag, type: 'string', required: true })}
                      className="text-blue-600 focus:ring-blue-500 w-4 h-4"
                    />
                    <span className="text-sm font-medium">Ручной ввод при генерации</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="mode"
                      checked={activeConfig.mode === 'unmapped'}
                      onChange={() => updateActiveConfig({ mode: 'unmapped' })}
                      className="text-gray-400 focus:ring-gray-400 w-4 h-4"
                    />
                    <span className="text-sm font-medium text-gray-500">Пропустить (Оставить пустым)</span>
                  </label>
                </div>

                <div className="flex-1">
                  {activeConfig.mode === 'db_field' && (
                    <div className="space-y-4 max-w-xl">
                      <label className="block text-sm font-medium text-gray-700">Выберите поле из БД</label>
                      <select
                        value={activeConfig.path || ''}
                        onChange={(e) => {
                          const selectedField = schemaFields.find(f => f.path === e.target.value);
                          if (selectedField) {
                            updateActiveConfig({
                              mode: 'db_field',
                              path: selectedField.path,
                              is_special: selectedField.is_special,
                              cardinality: selectedField.cardinality
                            });
                          }
                        }}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                      >
                        <option value="" disabled>-- Выберите поле --</option>
                        <optgroup label="Специальные / Системные поля">
                          {schemaFields.filter(f => f.is_special).map(f => (
                            <option key={f.path} value={f.path}>{f.display_name} ({f.path})</option>
                          ))}
                        </optgroup>
                        <optgroup label="Поля Заявки и Студентов">
                          {schemaFields.filter(f => !f.is_special).map(f => (
                            <option key={f.path} value={f.path}>{f.display_name} ({f.path})</option>
                          ))}
                        </optgroup>
                      </select>

                      {activeConfig.path && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-sm text-blue-800">
                            <strong>Путь:</strong> {activeConfig.path} <br/>
                            <strong>Тип:</strong> {activeConfig.is_special ? 'Специальное поле' : 'Поле БД'} <br/>
                            <strong>Формат:</strong> {activeConfig.cardinality === 'collection' ? 'Список (Коллекция)' : 'Одно значение (Скаляр)'}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {activeConfig.mode === 'manual' && (
                    <div className="space-y-5 max-w-xl">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Понятное название для пользователя</label>
                        <input
                          type="text"
                          value={activeConfig.label}
                          onChange={e => updateActiveConfig({ mode: 'manual', label: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none text-sm"
                          placeholder="Например: Номер доверенности"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Тип ввода</label>
                        <select
                          value={activeConfig.type}
                          onChange={e => updateActiveConfig({ mode: 'manual', type: e.target.value })}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none text-sm bg-white"
                        >
                          <option value="string">Текст</option>
                          <option value="date">Дата</option>
                          <option value="number">Число</option>
                        </select>
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer mt-2">
                        <input
                          type="checkbox"
                          checked={activeConfig.required}
                          onChange={e => updateActiveConfig({ mode: 'manual', required: e.target.checked })}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Обязательно для заполнения</span>
                      </label>
                    </div>
                  )}

                  {activeConfig.mode === 'unmapped' && (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-400 text-sm text-center">
                        Этот тег будет пропущен при генерации (останется пустым или сохранится как есть).<br/>
                        Выберите "Из Базы Данных" или "Ручной ввод", чтобы настроить его.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400">
                Выберите переменную слева для настройки
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateMapping;